import { Box, FormControlLabel } from '@mui/material'
import IconDropdown from 'components/IconDropdown/IconDropdown'
import { useFormikContext, Field } from 'formik'
import { t } from 'i18next'
import theme from 'lib/theme'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useRoutePlanning } from 'providers/RoutePlanningProvider'
import { useState, useEffect, SyntheticEvent, ChangeEvent } from 'react'
import { ORDER_STATUS } from 'types/api/order'
import { DeliveryShipment } from 'types/model/delivery'
import { DeliveriesForPlace } from 'types/model/order'
import { Dialog } from 'components/Dialog/Dialog'
import { RoutePlanningFormProps } from './OrderListing'
import { FormLabel, OrderCheckbox, OrderItem } from './elements'
import { listOfDeliveryNames } from './utils'
import { ListItem } from './ListItem'

export const ListOfAllOrders = ({
  ordersToDisplay,
  deliveries,
}: {
  ordersToDisplay: DeliveriesForPlace[]
  deliveries?: DeliveryShipment[]
}) => {
  const { values, setFieldValue, handleSubmit } =
    useFormikContext<RoutePlanningFormProps>()
  const [type, setType] = useState<string | null>(null)
  const [warning, setWarning] = useState(false)
  const { setSelectedOrders, markerHovered, setMarkerHovered, selectedOrders } =
    useRoutePlanning()

  useEffect(() => {
    setSelectedOrders([])
  }, [])

  useEffect(() => {
    setFieldValue('orders', selectedOrders)
  }, [selectedOrders])

  useEffect(() => {
    if (values.toggle) {
      const partIds = ordersToDisplay
        .filter((order) => order.parts[0].status !== ORDER_STATUS.DELIVERED)
        .map((order) => {
          const { latitude, longitude } =
            order.parts[0].deliveryMethod.service.address
          const latlng = `${latitude},${longitude}`
          return {
            orderId: order.parts[0].id,
            deliveryId: order.parts[0].deliveryId,
            latlng,
            routeName: order.routeName,
          }
        })
      setFieldValue('orders', partIds)
      setSelectedOrders([...partIds])
    } else {
      setFieldValue('orders', [])
      setSelectedOrders([])
    }
  }, [values.toggle])

  const handleDropdownAction = (type: string) => {
    setFieldValue('actionType', 'bulk_order_action')
    setFieldValue('submitType', type)
    handleSubmit()
  }

  const handleCloseAlert = (
    event?: SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return
    }

    setWarning(false)
  }

  const handleDropDownButtonClick = (type: string) => {
    const selectedOrderIds = values.orders.map((order) => order.orderId)
    const selectedOrders = ordersToDisplay.filter((order) =>
      selectedOrderIds.includes(order.parts[0].id)
    )
    if (
      selectedOrders.some(
        (order) => order.parts[0].status === ORDER_STATUS.DELIVERING
      )
    ) {
      setType(type)
      setWarning(true)
    } else {
      handleDropdownAction(type)
    }
  }

  const removeFromRouteOption = {
    value: `removeMultipleFromRoute`,
    label: t('routePlanning.removeFromRoute'),
  }

  const getOptions = () => {
    const routeNames = values.orders.map((order) => order.routeName)
    const orderIds = values.orders.map((order) => order.orderId)
    const checkedOrders = ordersToDisplay.filter((order) =>
      orderIds.includes(order.parts[0].id)
    )
    const cancelledOrders = checkedOrders.filter(
      (order) => order.orderStatus === ORDER_STATUS.CANCELLED
    )

    const options = [
      {
        value: 'createShipment',
        label: t('routePlanning.createNewRoute'),
      },
      ...listOfDeliveryNames(deliveries, routeNames),
    ]
    if (
      cancelledOrders.length > 0 &&
      checkedOrders.filter((order) => !order.routeIsInPlanning).length > 0
    ) {
      return [{ label: t('noOptionsAvailable'), value: 'disabled' }]
    }
    if (cancelledOrders.length > 0) {
      return [removeFromRouteOption]
    }
    if (checkedOrders.every((order) => order.routeIsInPlanning)) {
      options.push(removeFromRouteOption)
    }
    return options
  }

  return (
    <>
      <Box
        pt={1}
        pl={4}
        pr={3}
        pb={3}
        display='flex'
        justifyContent='space-between'>
        <Dialog
          dialogOpen={warning}
          handleConfirm={() => handleDropdownAction(type as string)}
          title={t('editOrderRoutes')}
          text={t('editOrdersText')}
          handleClose={handleCloseAlert}
          cancelText={t('cancel')}
          confirmText={t('confirm')}
        />
        <Field
          as={FormControlLabel}
          type='checkbox'
          name='toggle'
          control={<OrderCheckbox />}
        />
        {values.orders.length > 0 ? (
          <IconDropdown
            height='222px'
            isIconOnly
            disabled={getOptions()[0].value === 'disabled'}
            ariaLabel={t('routePlanning.bulkActionMenu')}
            onChange={(_, value) => handleDropDownButtonClick(value)}
            options={getOptions()}
            Icon={MoreVertIcon}
          />
        ) : null}
      </Box>
      {ordersToDisplay?.map((order) => {
        const routeNameIndex =
          ordersToDisplay
            .filter(
              (orderToDisplay) => orderToDisplay.routeName === order.routeName
            )
            .findIndex((element) => element.parts[0].id === order.parts[0].id) +
          1
        const backgroundColor = (() => {
          if (
            values.orders.find(
              (selectedOrder) => selectedOrder.orderId === order.parts[0].id
            )
          ) {
            return theme.palette.action.selected
          }
          if (markerHovered?.id === order.parts[0].id) {
            return theme.palette.action.hover
          }
          return theme.palette.commonColors.white
        })()
        const { latitude, longitude } =
          order.parts[0].deliveryMethod.service.address
        const latlng = `${latitude},${longitude}`
        return (
          <OrderItem
            onMouseOver={() =>
              setMarkerHovered({ id: order.parts[0].id, latlng })
            }
            onMouseOut={() => setMarkerHovered(null)}
            key={order.parts[0].id}
            defaultCursor
            sx={{ backgroundColor }}>
            <Field
              disabled={order.parts[0].status === ORDER_STATUS.DELIVERED}
              as={FormLabel}
              type='checkbox'
              name='orders'
              value={order}
              control={
                <OrderCheckbox
                  inputProps={{
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    'data-testid': `${order.parts[0].status}-order`,
                  }}
                  data-testid={`Route name ${order.routeName}`}
                />
              }
              checked={values.orders
                .map((value) => value.orderId)
                .includes(order.parts[0].id)}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const idx = values.orders
                  .map((value) => value.orderId)
                  .indexOf(order.parts[0].id)

                if (e.target.checked) {
                  const { latitude, longitude } =
                    order.parts[0].deliveryMethod.service.address
                  const latlng = `${latitude},${longitude}`
                  const changedOrders = [
                    ...values.orders,
                    ...(idx === -1
                      ? [
                          {
                            orderId: order.parts[0].id,
                            deliveryId: order.parts[0].deliveryId,
                            latlng,
                            routeName: order.routeName,
                          },
                        ]
                      : []),
                  ]
                  setFieldValue('orders', changedOrders)
                  setSelectedOrders(changedOrders)
                } else if (idx > -1) {
                  values.orders.splice(idx, 1)
                  const changedOrders = [...values.orders]
                  setFieldValue('orders', values.orders)
                  setSelectedOrders(changedOrders)
                }
              }}
              label={
                <ListItem
                  color={order.color}
                  routeName={order.routeName}
                  routeNameIndex={routeNameIndex}
                  partId={order.parts[0].id}
                  startTime={order.orderDeliveryTimes.startTime}
                  endTime={order.orderDeliveryTimes.endTime}
                  status={order.parts[0].status}
                  deliveryId={order.parts[0].deliveryId}
                  visibleId={order.parts[0].visibleId}
                />
              }
            />
          </OrderItem>
        )
      })}
    </>
  )
}
